import React, { useState } from "react"
import Paper from "../../ui/Paper"
import Input from "../../ui/Input"
import { useDispatch, useSelector } from "react-redux"
import {
  addUserAccount,
  selectCreatingUserAccount,
  selectUserAccountsRoles,
} from "./slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"
import Label from "../../ui/Label"
import Select from "../../ui/Select"
import FormError from "../../ui/FormError"
import FormGroup from "../../ui/FormGroup"
import Button from "../../ui/Button"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import Tip from "../../ui/Tip"
import InputRow from "../../ui/InputRow"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import IconButton from "../../ui/IconButton"
import ActionsFooter from "../../ui/ActionsFooter"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 25vw;
  min-width: 20rem;
  max-width: 40rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

/**
 * The form validator
 */
const schema = yup
  .object({
    display_name: yup.string().max(80).required("display name is required"),
    role: yup.string().max(100).required(),
    email: yup.string().when("role", {
      // Any non-telescope user must have email username
      is: "telescope",
      then: (schema) => schema.min(2).required(),
      otherwise: (schema) => schema.email().required("email is required"),
    }),
    ctx: yup.string().max(150),
    password: yup.string().required("password is required"),
    confirmation: yup
      .string()
      .oneOf([yup.ref("password"), null], "passwords must match"),
    active: yup.bool().default(true),
    password_reset_required: yup.bool().default(false),
    token_lifetime: yup.string(),
  })
  .required()

/**
 * Create a new user account
 *
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const CreateUserAccount = ({ handleCancel }) => {
  const dispatch = useDispatch()
  const [errorMessage, setErrorMessage] = useState()
  const [showPassword, setShowPassword] = useState(false)
  const roles = useSelector(selectUserAccountsRoles)
  const inProgressList = useSelector((state) =>
    selectCreatingUserAccount(state)
  )
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  })
  const onSubmit = (data) => {
    dispatch(addUserAccount(data))
      .unwrap()
      .then(() => {
        handleCancel()
      })
      .catch((e) => setErrorMessage(e?.error))
  }

  // Info about selected role
  const role = watch("role")
  const roleInfo = roles[role]

  return (
    <Panel>
      <h2>New User Account</h2>
      {inProgressList?._saveStatus === "creating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}

      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="email">Email (username):</Label>
            <Input
              type="text"
              {...register("email")}
              autoFocus
              placeholder="user@host.com"
            />
            <FormError>{errors.email?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="password">Password:</Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                {...register("password")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={(e) => {
                  setShowPassword(!showPassword)
                  e.preventDefault()
                }}
                secondary
              />
            </InputRow>
            <FormError>{errors.password?.message}</FormError>
            <Tip>
              &gt; 8 chars, &gt; 1 num, &gt; 1 upper, &gt; 1 special, sufficient
              complexity
            </Tip>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="ctx">Confirm password:</Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                {...register("confirmation")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={(e) => {
                  setShowPassword(!showPassword)
                  e.preventDefault()
                }}
                secondary
              />
            </InputRow>
            <FormError>{errors.confirmation?.message}</FormError>
          </FormGroup>
          <div></div>
          <FormGroup>
            <Label htmlFor="diaplayname">Display name:</Label>
            <Input type="text" {...register("display_name")} />
            <FormError>{errors.display_name?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="role">Role:</Label>
            <Select {...register("role")}>
              <option value="">Please select</option>
              {Object.values(roles).map((r, i) => (
                <option key={i} value={r.name}>
                  {r.name}
                </option>
              ))}
            </Select>
            {!roleInfo && <Tip>Determines user&apos;s permissions</Tip>}
            {roleInfo && (
              <p>
                <FontAwesomeIcon
                  color={roleInfo.colorCode}
                  icon={roleInfo.icon}
                />{" "}
                <span>{roleInfo.description}</span>
              </p>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="ctx">Who role is for (org URI):</Label>
            <Input type="text" {...register("ctx")} />
            <Tip>Leave blank to allow for all orgs</Tip>
            <FormError>{errors.ctx?.message}</FormError>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="role">Login token lifetime:</Label>
            <Select {...register("token_lifetime")}>
              <option value="">Not set (server default)</option>
              <option value="P7D">1 week</option>
              <option value="P1M">1 month</option>
              <option value="P1Y">1 year</option>
              <option value="P10Y">10 years</option>
              {/* TODO: Determine token lifetime options from server */}
            </Select>
            <Tip>How long login remains valid for</Tip>
            <FormError>{errors.token_lifetime?.message}</FormError>
          </FormGroup>

          <FormGroup>
            <Label>
              <input type="checkbox" {...register("active")} checked />
              Activate user now
            </Label>
          </FormGroup>
          <FormGroup>
            <Label>
              <input type="checkbox" {...register("password_reset_required")} />
              Force user to reset password
            </Label>
          </FormGroup>
          {(inProgressList?._saveStatus == "rejected" || errorMessage) && (
            <ErrorMsg
              msg={
                inProgressList._errorMessage ||
                errorMessage ||
                "an error occurred creating user account, try a more complex password or contact support if the problem persists"
              }
              showIcon={true}
            />
          )}
          <ActionsFooter>
            <Button type="submit" active>
              Create user
            </Button>
            {handleCancel && <Button onClick={handleCancel}>Cancel</Button>}
          </ActionsFooter>
        </form>
      </FormContainer>
    </Panel>
  )
}

CreateUserAccount.propTypes = {
  handleCancel: PropTypes.func, // Cancel the creation of a priority list
}
export default CreateUserAccount
